import { Archive, CalendarRange, Download, Plus, Search, Settings, Upload } from "lucide-react";
import { useState } from "react";
import { JobTypeChip } from "@/components/ui/JobTypeChip";
import {
  type ResourcePreview,
  useArchiveWorkOrder,
  useWorkOrders,
  type WorkOrder,
} from "@/hooks/use-work-orders";
import { ImportWorkOrdersDialog } from "./ImportWorkOrdersDialog";
import { WorkOrderFormDialog } from "./WorkOrderFormDialog";

interface ArchiveModalProps {
  wo: WorkOrder | null;
  onClose: () => void;
  onConfirm: (id: string) => void;
  isPending: boolean;
}

function ArchiveModal({ wo, onClose, onConfirm, isPending }: ArchiveModalProps) {
  if (!wo) return null;
  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">Archive this work order?</h2>
        </div>
        <div className="modal__body">
          <p>
            <strong>WO {wo.woNumber}</strong> — {wo.client} will no longer appear in the time entry
            dropdown. Engineers cannot log new time against it.
          </p>
          <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 0 }}>
            Historical time entries linked to this WO will not be affected.
          </p>
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => onConfirm(wo.id)}
            disabled={isPending}
          >
            {isPending ? "Archiving…" : "Archive Work Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function formatDateShort(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ScheduleCell({ start, end }: { start: string | null; end: string | null }) {
  if (!start && !end) {
    return <span className="t-muted t-small">—</span>;
  }
  const left = start ? formatDateShort(start) : "?";
  const right = end ? formatDateShort(end) : "?";
  return (
    <span className="schedule-cell">
      <CalendarRange size={13} />
      {start && end ? `${left} – ${right}` : start ? `From ${left}` : `Until ${right}`}
    </span>
  );
}

function ResourcesCell({ count, preview }: { count: number; preview: ResourcePreview[] }) {
  if (count === 0) return <span className="t-muted t-small">—</span>;
  const overflow = count - preview.length;
  return (
    <span className="avatar-stack">
      {preview.map((r) => (
        <span key={r.id} className="avatar-stack__bubble" title={r.name}>
          {initials(r.name)}
        </span>
      ))}
      {overflow > 0 && <span className="avatar-stack__more">+{overflow}</span>}
    </span>
  );
}

export function WorkOrdersAdmin() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "archived">("all");
  const [confirmArchive, setConfirmArchive] = useState<WorkOrder | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const { data } = useWorkOrders({
    search: search.length >= 2 ? search : undefined,
    status: statusFilter,
  });
  const workOrders = data?.data ?? [];
  const total = data?.total ?? 0;

  const allWOs = useWorkOrders({ status: "all", limit: 200 });
  const activeCount = (allWOs.data?.data ?? []).filter((w) => w.active).length;
  const archivedCount = (allWOs.data?.data ?? []).filter((w) => !w.active).length;

  const archiveWO = useArchiveWorkOrder();

  function handleArchiveConfirm(id: string) {
    archiveWO.mutate(id, { onSuccess: () => setConfirmArchive(null) });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header__eyebrow">
            <Settings size={14} /> Admin
          </div>
          <h1 className="page-header__title">Work Orders</h1>
          <p className="page-header__sub">Manage and import active work order records.</p>
        </div>
        <div className="wo-toolbar">
          <button type="button" className="btn btn--secondary">
            <Download size={14} className="btn__icon" /> Template
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => setImportOpen(true)}>
            <Upload size={14} className="btn__icon" /> Import WO Log
          </button>
          <button type="button" className="btn btn--primary" onClick={() => setCreateOpen(true)}>
            <Plus size={14} className="btn__icon" /> New Work Order
          </button>
        </div>
      </div>

      <div className="wo-meta-strip">
        <span>
          <span className="wo-meta-strip__value">{activeCount}</span> active
        </span>
        <span className="wo-meta-strip__sep">·</span>
        <span>
          <span className="wo-meta-strip__value">{archivedCount}</span> archived
        </span>
        <span className="wo-meta-strip__sep">·</span>
        <span>
          Last imported <span className="wo-meta-strip__value">Apr 22, 2026 · 9:14 AM</span> by{" "}
          <span className="wo-meta-strip__value">Dana Walsh</span>
        </span>
      </div>

      <div className="card">
        <div className="card__header">
          <h2 className="h-section">Work Orders ({total})</h2>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="search-input">
              <Search size={14} />
              <input
                placeholder="Search WO# or client…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="segmented" style={{ width: 260 }}>
              {(["all", "active", "archived"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`segmented__option${statusFilter === s ? " is-active" : ""}`}
                  onClick={() => setStatusFilter(s)}
                  style={{ padding: "8px 12px", fontSize: 13 }}
                >
                  {s[0].toUpperCase()}
                  {s.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="card__body card__body--flush">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>WO Number</th>
                <th>Client</th>
                <th style={{ width: 90 }}>Job Type</th>
                <th style={{ width: 150 }}>Schedule</th>
                <th style={{ width: 130 }}>Resources</th>
                <th style={{ width: 90 }}>Billable</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 110, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((w) => (
                <tr
                  key={w.id}
                  className={`row-clickable${w.active ? "" : " is-archived"}`}
                  onClick={() => setEditId(w.id)}
                >
                  <td className="wo-num">{w.woNumber}</td>
                  <td>{w.client}</td>
                  <td>
                    <JobTypeChip type={w.jobType} />
                  </td>
                  <td>
                    <ScheduleCell start={w.startDate} end={w.endDate} />
                  </td>
                  <td>
                    <ResourcesCell count={w.resourceCount} preview={w.resourcePreview} />
                  </td>
                  <td>
                    <span className={`badge ${w.billable ? "badge--billable" : "badge--overhead"}`}>
                      {w.billable ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    {w.active ? (
                      <span className="badge badge--approved">Active</span>
                    ) : (
                      <span className="badge badge--draft">Archived</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditId(w.id)}
                      >
                        Edit
                      </button>
                      {w.active && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setConfirmArchive(w)}
                        >
                          <Archive size={14} /> Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {workOrders.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    style={{ textAlign: "center", padding: "32px", color: "var(--muted)" }}
                  >
                    No work orders match your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ArchiveModal
        wo={confirmArchive}
        onClose={() => setConfirmArchive(null)}
        onConfirm={handleArchiveConfirm}
        isPending={archiveWO.isPending}
      />

      <WorkOrderFormDialog
        open={createOpen}
        mode={{ kind: "create" }}
        onOpenChange={setCreateOpen}
      />

      <WorkOrderFormDialog
        open={editId !== null}
        mode={editId ? { kind: "edit", woId: editId } : { kind: "create" }}
        onOpenChange={(open) => {
          if (!open) setEditId(null);
        }}
      />

      <ImportWorkOrdersDialog open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
}
